export type IndustryKey =
  | "msp" | "landscaping" | "pest_control" | "hvac" | "cleaning"
  | "plumbing" | "electrical" | "security" | "pool_spa" | "fire_protection"
  | "elevator" | "property_mgmt" | "home_health" | "legal" | "construction"
  | "veterinary" | "kitchen_equipment" | "custom"
  | "marketing_agency" | "pr_firm" | "design_studio" | "advertising_agency"
  | "web_agency" | "seo_content_agency" | "video_production"
  | "photography_studio" | "branding_agency" | "event_management";

export interface Industry {
  key: IndustryKey;
  label: string;
  brand: string;
  icon: string;
  description: string;
}

export const INDUSTRIES: Industry[] = [
  { key: "msp",              label: "IT Managed Services",       brand: "Helix PSA",          icon: "🖥️",  description: "Tickets, assets, MSP operations" },
  { key: "landscaping",      label: "Lawn & Landscaping",        brand: "Helix Grounds™",     icon: "🌿",  description: "Work orders, crews, route management" },
  { key: "pest_control",     label: "Pest Control",              brand: "Helix Pest™",        icon: "🐛",  description: "Service orders, chemical compliance" },
  { key: "hvac",             label: "HVAC / Mechanical",         brand: "Helix Mechanical™",  icon: "❄️",  description: "Work orders, equipment maintenance" },
  { key: "cleaning",         label: "Commercial Cleaning",       brand: "Helix Clean™",       icon: "✨",  description: "Service orders, crew routing" },
  { key: "plumbing",         label: "Plumbing",                  brand: "Helix Flow™",        icon: "🔧",  description: "Job orders, emergency dispatch" },
  { key: "electrical",       label: "Electrical",                brand: "Helix Current™",     icon: "⚡",  description: "Work orders, permit compliance" },
  { key: "security",         label: "Security Guard Services",   brand: "Helix Sentinel™",    icon: "🛡️",  description: "Post assignments, incident reports" },
  { key: "pool_spa",         label: "Pool & Spa Service",        brand: "Helix Aqua™",        icon: "🏊",  description: "Service visits, chemical logs" },
  { key: "fire_protection",  label: "Fire Protection",           brand: "Helix Guard™",       icon: "🔥",  description: "Inspection orders, deficiency tracking" },
  { key: "elevator",         label: "Elevator / Escalator",      brand: "Helix Lift™",        icon: "🏗️",  description: "Service orders, regulatory compliance" },
  { key: "property_mgmt",    label: "Property Management",       brand: "Helix Custodian™",   icon: "🏢",  description: "Maintenance requests, tenant management" },
  { key: "home_health",      label: "Home Health Care",          brand: "Helix Medicus™",     icon: "🏥",  description: "Care visits, caregiver scheduling" },
  { key: "legal",            label: "Legal Services",            brand: "Helix Counselor™",   icon: "⚖️",  description: "Matter tasks, billable hours" },
  { key: "construction",     label: "Construction",              brand: "Helix Construct™",   icon: "🏗️",  description: "Work orders, trade crews, bids" },
  { key: "veterinary",       label: "Veterinary Mobile",         brand: "Helix Equine™",      icon: "🐴",  description: "Clinical visits, farm routing" },
  { key: "kitchen_equipment",label: "Commercial Kitchen Equip.", brand: "Helix Kitchen™",     icon: "🍳",  description: "Service calls, restaurant clients" },
  { key: "marketing_agency",   label: "Marketing / Social Media Agency", brand: "Helix Reach™",   icon: "📢",  description: "Campaigns, creative requests, client engagements" },
  { key: "pr_firm",            label: "PR Firm",                   brand: "Helix Press™",     icon: "📰",  description: "Media placements, pitches, retainer accounts" },
  { key: "design_studio",      label: "Design Studio",             brand: "Helix Studio™",    icon: "🎨",  description: "Creative projects, design reviews, retainers" },
  { key: "advertising_agency", label: "Advertising Agency",        brand: "Helix Ads™",       icon: "📺",  description: "Campaigns, media buys, creative retainers" },
  { key: "web_agency",         label: "Web Design & Development Agency", brand: "Helix Web™",   icon: "🌐",  description: "Builds, care plans, site maintenance" },
  { key: "seo_content_agency", label: "SEO & Content Agency",      brand: "Helix SEO™",       icon: "🔍",  description: "Engagements, optimisations, content calendars" },
  { key: "video_production",   label: "Video Production Company",  brand: "Helix Reel™",      icon: "🎬",  description: "Productions, shoots, post-production workflows" },
  { key: "photography_studio", label: "Photography Studio",        brand: "Helix Lens™",      icon: "📷",  description: "Shoots, sessions, galleries, packages" },
  { key: "branding_agency",    label: "Branding Agency",           brand: "Helix Brand™",     icon: "🏷️",  description: "Brand engagements, workshops, stewardship" },
  { key: "event_management",   label: "Event Management",          brand: "Helix Events™",    icon: "🎟️",  description: "Events, site visits, vendor coordination" },
  { key: "custom",             label: "Custom",                    brand: "Custom Vocabulary",  icon: "✏️",  description: "Define your own terminology" },
];

// Client-side fallback presets used when the apply_industry_vocabulary RPC
// is unavailable. Keys map to { term_value, term_plural }.
export const INDUSTRY_PRESETS: Partial<Record<IndustryKey, Record<string, { value: string; plural: string }>>> = {
  msp: {
    ticket: { value: "Ticket", plural: "Tickets" },
    technician: { value: "Technician", plural: "Technicians" },
    asset: { value: "Asset", plural: "Assets" },
  },
  landscaping: {
    ticket: { value: "Work Order", plural: "Work Orders" },
    technician: { value: "Crew Member", plural: "Crew Members" },
    asset: { value: "Property", plural: "Properties" },
  },
  pest_control: {
    ticket: { value: "Service Order", plural: "Service Orders" },
    technician: { value: "Technician", plural: "Technicians" },
    asset: { value: "Site", plural: "Sites" },
  },
  hvac: {
    ticket: { value: "Work Order", plural: "Work Orders" },
    technician: { value: "Technician", plural: "Technicians" },
    asset: { value: "Equipment", plural: "Equipment" },
  },
  cleaning: {
    ticket: { value: "Service Order", plural: "Service Orders" },
    technician: { value: "Cleaner", plural: "Cleaners" },
    asset: { value: "Property", plural: "Properties" },
  },
  plumbing: {
    ticket: { value: "Job Order", plural: "Job Orders" },
    technician: { value: "Plumber", plural: "Plumbers" },
    asset: { value: "Fixture", plural: "Fixtures" },
  },
  electrical: {
    ticket: { value: "Work Order", plural: "Work Orders" },
    technician: { value: "Electrician", plural: "Electricians" },
    asset: { value: "Panel", plural: "Panels" },
  },
  security: {
    ticket: { value: "Post Order", plural: "Post Orders" },
    technician: { value: "Officer", plural: "Officers" },
    asset: { value: "Post", plural: "Posts" },
  },
  pool_spa: {
    ticket: { value: "Service Visit", plural: "Service Visits" },
    technician: { value: "Technician", plural: "Technicians" },
    asset: { value: "Pool", plural: "Pools" },
  },
  fire_protection: {
    ticket: { value: "Inspection Order", plural: "Inspection Orders" },
    technician: { value: "Inspector", plural: "Inspectors" },
    asset: { value: "System", plural: "Systems" },
  },
  elevator: {
    ticket: { value: "Service Order", plural: "Service Orders" },
    technician: { value: "Mechanic", plural: "Mechanics" },
    asset: { value: "Unit", plural: "Units" },
  },
  property_mgmt: {
    ticket: { value: "Maintenance Request", plural: "Maintenance Requests" },
    technician: { value: "Maintenance Tech", plural: "Maintenance Techs" },
    asset: { value: "Unit", plural: "Units" },
  },
  home_health: {
    ticket: { value: "Care Visit", plural: "Care Visits" },
    technician: { value: "Caregiver", plural: "Caregivers" },
    asset: { value: "Patient", plural: "Patients" },
  },
  legal: {
    ticket: { value: "Matter Task", plural: "Matter Tasks" },
    technician: { value: "Attorney", plural: "Attorneys" },
    asset: { value: "Matter", plural: "Matters" },
  },
  construction: {
    ticket: { value: "Work Order", plural: "Work Orders" },
    technician: { value: "Crew Member", plural: "Crew Members" },
    asset: { value: "Site", plural: "Sites" },
  },
  veterinary: {
    ticket: { value: "Clinical Visit", plural: "Clinical Visits" },
    technician: { value: "Veterinarian", plural: "Veterinarians" },
    asset: { value: "Animal", plural: "Animals" },
  },
  kitchen_equipment: {
    ticket: { value: "Service Call", plural: "Service Calls" },
    technician: { value: "Technician", plural: "Technicians" },
    asset: { value: "Equipment", plural: "Equipment" },
  },
  marketing_agency: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Strategist", plural: "Strategists" },
    asset: { value: "Brand", plural: "Brands" },
  },
  pr_firm: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Publicist", plural: "Publicists" },
    asset: { value: "Brand", plural: "Brands" },
  },
  design_studio: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Designer", plural: "Designers" },
    asset: { value: "Brand", plural: "Brands" },
  },
  advertising_agency: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Creative", plural: "Creatives" },
    asset: { value: "Brand", plural: "Brands" },
  },
  web_agency: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Developer", plural: "Developers" },
    asset: { value: "Website", plural: "Websites" },
  },
  seo_content_agency: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Specialist", plural: "Specialists" },
    asset: { value: "Property", plural: "Properties" },
  },
  video_production: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Producer", plural: "Producers" },
    asset: { value: "Location", plural: "Locations" },
  },
  photography_studio: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Photographer", plural: "Photographers" },
    asset: { value: "Location", plural: "Locations" },
  },
  branding_agency: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Strategist", plural: "Strategists" },
    asset: { value: "Brand", plural: "Brands" },
  },
  event_management: {
    ticket: { value: "Request", plural: "Requests" },
    technician: { value: "Coordinator", plural: "Coordinators" },
    asset: { value: "Venue", plural: "Venues" },
  },
};
